import { AITaskResponseResultData, collaborateApi, useAcceptAiTaskMutation, useDismissAiTaskMutation, useUploadPredesignMutation } from "api/collaborateApi";
import { useGetPagesQuery } from "api/siteApi";
import { Editor } from "editor";
import { ChevronDown, Plus, Sparkles } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { CollaborateTaskData } from "types/api/collaborate";
import { Badge, Button, Collapsible } from "ui";
import { normalizeUrl } from "utils/proxyUtils";
import aiBotData from "../../data/ai-bot-data.json";
import { AtariMatesAnalysis } from "../../hooks/use-analyze-view-port";
import useElementContextAnalyzer, { ClarityAnalysisData, EnhancedElementProperties } from "../../hooks/use-element-analyzer";
import useGetPageId from "../../hooks/use-get-pageid";
import { ScreenCapture } from "../../ScreenCapture";
import { useAppDispatch } from "../../store";
import { getSelectedSiteId, removeAiTask } from "../../store/siteInfoSlice";
import { addTask, replaceTask } from "../../store/tasksSlice";

const issue = aiBotData.data.issues[0];
type BadgeVariant = "lexi" | "pixel" | "glitch" | "navi" | "index" | "primary" | null | undefined;

const variantMap: Record<string, BadgeVariant> = {
    "Lexi": "lexi",
    "Pixel": "pixel",
    "Glitch": "glitch",
    "Navi": "navi",
    "Index": "index",
};
const variant: BadgeVariant = variantMap[issue.expert] || "primary";

// External actions for accepting/dismissing an AI task (used by hover panel swipe)
export const useAiTaskActions = (item: Partial<AITaskResponseResultData> | undefined) => {
    const siteId = useSelector(getSelectedSiteId);
    const dispatch = useAppDispatch();
    const [uploadPredesign] = useUploadPredesignMutation();
    const { data } = useGetPagesQuery({ siteId: siteId || '' }, { skip: !siteId });
    const [acceptLoading, setAcceptLoading] = useState(false);
    const [acceptAiTask, { isLoading: isLoadingAccept }] = useAcceptAiTaskMutation();
    const [dismissAiTask, { isLoading: isLoadingDismiss }] = useDismissAiTaskMutation();
    const { currentPage } = useGetPageId();
    const elementAnalyzer = useElementContextAnalyzer();


    const dataURItoBlob = useCallback((dataURI: string): Blob => {
        const mime = dataURI.split(',')[0].split(':')[1].split(';')[0];
        const binary = atob(dataURI.split(',')[1]);
        const array = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            array[i] = binary.charCodeAt(i);
        }
        return new Blob([array], { type: mime });
    }, []);

    const uploadScreenshot = async (file: File, uploadUrl: string): Promise<Response> => {
        const contentType = file.type || 'image/png';
        return fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': contentType,
            },
        });
    };

    const createOptimisticTask = useCallback((it: Partial<AITaskResponseResultData>) => {
        const optimisticTaskData: CollaborateTaskData = {
            collaborators: [],
            height: 0,
            width: 0,
            id: Number(it.id) || 0,
            latest_comment: {
                author: {
                    avatar: '',
                    email: '',
                    id: it.expert || '',
                    user_name: it.expert || '',
                },
                comments: it.description || '',
                comment_type: 'normal_text',
                days: 'posting...',
                id: crypto.randomUUID(),
            },
            notify_wp_feedback_users: [],
            page_approved_at: new Date().toISOString(),
            task_id: it.id || '',
            task_title: it.issueType || '',
            task_page_title: it.description || '',
            task_comment_id: 0,
            task_comment_message: it.description || '',
            task_created_by: it.expert || '',
            task_created_time: new Date().toISOString(),
            task_created_time_ago: 'posting...',
            task_type: ['Page'],
            is_complete: false,
            is_internal: false,
            is_unread: true,
            task_status: 'open',
            task_priority: 'low',
            page_url: window.location.href,
            task_page_url: window.location.href,
            task_elementX: 0,
            task_elementY: 0,
            task_element_path: it.element?.path || '',
            graphic_id: null,
            task_tags: [],
            is_approved: 0,
            atarim_task_screenshot: '',
            is_pinned: false,
            wpf_task_screenshot: '',
            task_position: [],
            is_guest: false,
        };

        return optimisticTaskData;
    }, []);

    const captureScreenshot = useCallback(async (): Promise<File | null> => {
        try {
            const sc = new ScreenCapture();
            const data = await sc.capture({ useHtml2Canvas: true });
            if (!data) return null;
            const blob = dataURItoBlob(data);
            return new File([blob], 'screenshot.png', { type: blob.type });
        } catch (error) {
            console.error('Error capturing screenshot:', error);
            return null;
        }
    }, [dataURItoBlob]);

    const pageData = useMemo(() => {
        if (!data) return;
        return data?.data?.pages?.page?.find(p => p?.url && window.location.href.includes(normalizeUrl(p.url)));
    }, [data]);

    const acceptTask = async (id: string) => {
        try {
            if (!item || !id) {
                toast.error('Invalid task data. Please try again.');
                return;
            }

            setAcceptLoading(true);

            const screenshot = await captureScreenshot();
            if (!screenshot) {
                toast.error('Failed to capture screenshot. Please try again.');
                setAcceptLoading(false);
                return;
            }

            let uploadResponse;
            try {
                uploadResponse = await uploadPredesign({ files: [{ name: 'screenshot.png' }] }).unwrap();
            } catch (_error) {
                toast.error('Failed to prepare upload. Please try again.');
                setAcceptLoading(false);
                return;
            }

            if (!uploadResponse?.result?.[0]?.url) {
                toast.error('Failed to get upload URL. Please try again.');
                setAcceptLoading(false);
                return;
            }

            const uploadResult = await uploadScreenshot(screenshot, uploadResponse.result[0].url);
            if (uploadResult.status !== 200 && uploadResult.status !== 201) {
                toast.error(`Upload failed with status: ${uploadResult.status}. Please try again.`);
                setAcceptLoading(false);
                return;
            }

            const pageUrl = currentPage?.target_url || pageData?.target_url || window.location.href;
            const pageTitle = pageData?.label || currentPage?.label || document.title || 'Unknown Page';

            let scrollTo = 0;
            let viewportX = '1920';
            let viewportY = '1080';
            const elementPath = item?.element?.path || '';
            let analysisData: ClarityAnalysisData | null = null;

            try {
                if (elementPath) {
                    analysisData = await elementAnalyzer.analyzeElementForClarity(elementPath);

                    if (analysisData) {
                        const elementProperties: EnhancedElementProperties | AtariMatesAnalysis =
                            elementAnalyzer.formatForWorker(analysisData);

                        const elementY = elementProperties?.position?.y || 0;
                        const elementHeight = elementProperties?.position?.height || 0;
                        const viewportHeight = analysisData.pageMetadata?.viewport?.height || window.innerHeight;

                        scrollTo = elementY + elementHeight / 2 - viewportHeight / 2;
                    }
                }
            } catch (_error) {
                console.warn('Element analysis failed, using fallback values:', _error);
            }

            try {
                viewportX = (window.screen?.width || window.innerWidth || 1920).toString();
                viewportY = (window.screen?.height || window.innerHeight || 1080).toString();
            } catch (_error) {
                console.warn('Failed to get viewport dimensions, using fallback values:', _error);
            }

            const browserUserAgent = navigator?.userAgent || 'Unknown Browser';
            const browserVersion = navigator?.appVersion || 'Unknown Version';

            const optimisticTask = createOptimisticTask(item);

            dispatch(addTask(optimisticTask));
            dispatch(removeAiTask(id));

            try {
                const res = await acceptAiTask({
                    pageUrl: pageUrl,
                    pageTitle: pageTitle,
                    scroll_to: scrollTo,
                    viewportWidth: window.innerWidth,
                    viewportHeight: window.innerHeight,
                    issueId: id,
                    taskConfigAuthorBrowser: browserUserAgent,
                    taskConfigAuthorBrowserVersion: browserVersion,
                    taskConfigAuthorResX: viewportX,
                    taskConfigAuthorResY: viewportY,
                    taskElementPath: elementPath,
                }).unwrap();

                dispatch(replaceTask({ task_id: id, task: res }));
                collaborateApi.util.invalidateTags(['AiDesignReviewIssues']);
                toast.success('AI task accepted successfully!');

            } catch (error: unknown) {
                type ApiError = { data?: { message?: string } };
                const apiError = error as ApiError;
                const errorMessage = typeof apiError?.data?.message === 'string' ? apiError.data!.message! : '';

                if (errorMessage.includes('has already been accepted')) {
                    toast.info(errorMessage || 'Task has already been accepted.');
                    dispatch(removeAiTask(id));
                    collaborateApi.util.invalidateTags(['AiDesignReviewIssues']);
                } else {
                    toast.error('Failed to accept AI task. Please try again.');
                    console.error('Error accepting AI task:', error);
                }
            }

        } catch (error) {
            toast.error('Failed to accept AI task. Please try again.');
            console.error('Error accepting AI task:', error);
        } finally {
            setAcceptLoading(false);
        }
    };

    const dismissTask = (id: string) => {
        dismissAiTask(id).unwrap().then(() => {
            dispatch(removeAiTask(id));
            collaborateApi.util.invalidateTags(['AiDesignReviewIssues']);
        }).catch((error: unknown) => {
            const err = error as { data?: { message?: string } };
            if (err?.data?.message?.includes('issue is not pending')) {
                toast.info(err?.data?.message || 'Failed to dismiss AI task. Please try again.');
                dispatch(removeAiTask(id));
                collaborateApi.util.invalidateTags(['AiDesignReviewIssues']);
                return;
            }
            toast.error('Failed to dismiss AI task. Please try again.');
        });
    };

    return {
        acceptTask,
        dismissTask,
        isLoadingAccept,
        isLoadingDismiss,
        acceptLoading,
    } as const;
};

export const AiTask = ({ item }: { item: Partial<AITaskResponseResultData> | undefined }) => {
    const { t } = useTranslation();
    const { acceptTask: _acceptTaskUnused, dismissTask: _dismissTaskUnused, isLoadingAccept, isLoadingDismiss, acceptLoading } = useAiTaskActions(item);
    const [collapsibles, setCollapsibles] = useState<{ [key: number]: boolean }>({});


    return (
        <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
                <div className="flex flex-row gap-2 items-center w-full">
                    <div className="h-9 w-9 bg-tertiary rounded-md">
                        <img src={`https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/${item?.expert?.toLowerCase()}.png`} alt={item?.expert || ''} className="h-full w-full rounded-md" />
                    </div>
                    <div className="flex flex-col gap-1 grow leading-none">
                        <div className="flex gap-1 justify-between grow">
                            <div className="font-medium leading-tight text-textcolor">
                                {item?.expert}
                            </div>
                            <Badge
                                variant={variant}
                                size="sm"
                                leftIcon={<Sparkles size={12} />}
                                className="h-5 gap-1 truncate max-w-48"
                            >
                                {item?.badge}
                            </Badge>

                        </div>
                        <div className="text-xs flex flex-row gap-0.5 text-textcolor items-center leading-none">
                            <span className="max-w-52 truncate">{item?.issueType}</span></div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-2 max-h-[450px] overflow-y-auto">
                {item?.description &&
                    <Editor
                        content={item?.description}
                        editable={false}
                        className=""
                    />
                    // <span
                    //     className="relative prose dark:prose-invert dark:text-textcolor prose-code:text-primary-foreground prose-sm max-w-none w-full word-break prose-code:text-sm prose-code:font-normal prose-pre:bg-gray-100 prose-p:my-0 [&>p>a]:text-textcolor max-h-[450px] overflow-y-auto"
                    //     dangerouslySetInnerHTML={{ __html: item?.description }}>
                    //     {/* XSS the description */}
                    // </span>
                }
                {item?.extraContent && (
                    <div className='relative prose dark:prose-invert dark:text-textcolor prose-code:text-primary-foreground prose-sm max-w-none w-full word-break prose-code:text-sm prose-code:font-normal prose-pre:bg-gray-100 prose-p:my-0 [&>p>a]:text-textcolor'>
                        {item?.extraContent.map((content, index) => (
                            <div key={index}>
                                <Collapsible open={collapsibles[index]} onOpenChange={() => setCollapsibles({ ...collapsibles, [index]: !collapsibles[index] })}>
                                    <Collapsible.Trigger asChild>
                                        <Button
                                            variant="ghost"
                                            className="p-0 hover:bg-transparent -mt-1"
                                            rightIcon={
                                                <motion.span
                                                    animate={{ rotate: collapsibles[index] ? 180 : 0 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    <ChevronDown size={16} />
                                                </motion.span>
                                            }
                                            size="sm"
                                        >
                                            {content.title}
                                        </Button>
                                    </Collapsible.Trigger>
                                    <AnimatePresence >
                                        {collapsibles[index] && (
                                            <Collapsible.Content asChild className='mb-2' forceMount>
                                                <motion.div
                                                    key="collapsible-content"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                                                    className="overflow-hidden"
                                                >
                                                    <motion.div
                                                        key="list"
                                                        initial="hidden"
                                                        animate={collapsibles[index] ? 'visible' : 'hidden'}
                                                        variants={{
                                                            visible: {
                                                                transition: {
                                                                    delayChildren: 0.2,
                                                                    staggerChildren: 0.15,
                                                                },
                                                            },
                                                            hidden: {},
                                                        }}
                                                        className="text-sm text-textcolor flex flex-col gap-1"
                                                    >
                                                        <AnimatePresence mode="wait">

                                                            <motion.div

                                                                variants={{
                                                                    hidden: { opacity: 0, y: -10 },
                                                                    visible: {
                                                                        opacity: 1,
                                                                        y: 0,
                                                                        transition: {
                                                                            duration: 0.4,
                                                                            ease: [0.4, 0, 0.2, 1],
                                                                        },
                                                                    },
                                                                }}
                                                                dangerouslySetInnerHTML={{ __html: content.content }} />

                                                        </AnimatePresence>
                                                    </motion.div>
                                                </motion.div>
                                            </Collapsible.Content>
                                        )}
                                    </AnimatePresence>
                                </Collapsible>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="flex gap-2 w-full">
                <Button
                    variant="outline"
                    size="sm"
                    loading={isLoadingDismiss}
                    className="w-full"
                    onClick={() => _dismissTaskUnused(item?.id || '')}
                >
                    {t('ai_task.dismiss', 'Hide')}
                </Button>
                <Button
                    size="sm"
                    leftIcon={<Plus size={18} />}
                    className="w-full"
                    effect="shiny"
                    loading={isLoadingAccept || acceptLoading}
                    onClick={() => _acceptTaskUnused(item?.id || '')}
                    tooltip={t('ai_task.accept_tooltip', 'Keep to action or start a chat if you need clarity - with {{expert}}, your InnerCircle, or your human team.', { expert: item?.expert })}
                >
                    {t('ai_task.accept', 'Keep')}
                </Button>
            </div>

        </div>
    );
};
